import { prepareHeaders } from "./";
import { submitActivation } from "./activation";
import cincolabdataEndpoints from "./cincolabdata.endpoints";
import { getUserByEmail } from "./search";

export interface WaiverFormBooleanValue {
  value: boolean;
  required: boolean;
  error?: string;
  is_full?: boolean;
}

export interface WaiverFormStringValue {
  value: string;
  required: boolean;
  error?: string;
  is_full?: boolean;
}

export interface WaiverFormFileValue {
  value: File | null;
  required: boolean;
  error?: string;
  is_full?: boolean;
}

interface WaiverFormOption {
  label: { en: string; fr: string };
  value: string;
}

interface WaiverFormVehicleValue extends WaiverFormStringValue {
  options: WaiverFormOption[];
}

interface TestDriver {
  signature: WaiverFormStringValue;
  first_name: WaiverFormStringValue;
  last_name: WaiverFormStringValue;
  email: WaiverFormStringValue;
  driver_license: WaiverFormStringValue;
  driver_license_issuer: WaiverFormStringValue;
  driver_license_front: WaiverFormFileValue;
  driver_license_back: WaiverFormFileValue;
  phone: WaiverFormStringValue;
  address: WaiverFormStringValue;
  city: WaiverFormStringValue;
  province: WaiverFormStringValue;
  zip: WaiverFormStringValue;
  is_driver: WaiverFormBooleanValue;
  vehicule: WaiverFormVehicleValue;
  marketing_opt_in: WaiverFormBooleanValue;
  date: WaiverFormStringValue;
  driver_license_shown: WaiverFormBooleanValue;
}

interface Tutor {
  signature: WaiverFormStringValue;
  first_name: WaiverFormStringValue;
  last_name: WaiverFormStringValue;
  address: WaiverFormStringValue;
  city: WaiverFormStringValue;
  province: WaiverFormStringValue;
  zip: WaiverFormStringValue;
}

interface Signature {
  image: string;
  date: string;
}

interface License {
  license_number?: string;
  issuer?: string;
  front_image?: string;
  back_image?: string;
  entry_id?: string;
}

interface WaiverForm {
  test_driver: TestDriver;
  tutor: Tutor;
}

/**
 * Submits the waiver form to the API
 * @param instanceKey - Instance key of the event
 * @param testDriver - Test driver informations
 * @param tutor - Tutor informations
 * @returns Returns a promise that resolves when the waiver form has been submitted
 */
const submitWaiverForm = async (
  instanceKey: string,
  testDriver: TestDriver,
  tutor?: Tutor,
): Promise<string> => {
  const response = await fetch(cincolabdataEndpoints.public.form(instanceKey), {
    method: "POST",
    headers: prepareHeaders(),
    mode: "cors",
    body: JSON.stringify({
      email: testDriver.email.value,
      answers: {
        "LAST NAME": testDriver.last_name.value,
        "FIRST NAME": testDriver.first_name.value,
        PHONE: testDriver.phone.value,
        "DRIVER LICENSE NUMBER": testDriver.driver_license.value,
        "DRIVER LICENSE NUMBER ISSUER": testDriver.driver_license_issuer.value,
        "STREET ADDRESS": testDriver.address.value,
        CITY: testDriver.city.value,
        STATE: testDriver.province.value,
        ZIP: testDriver.zip.value,
        COUNTRY: "CAN",
        VEHICULE: testDriver.vehicule.value,
        "IS DRIVER": testDriver.is_driver.value,

        Q799E: "0799",
        A799E: testDriver.marketing_opt_in.value ? "A" : "H",
        Q799M: "0799",
        A799M: "E",

        ...(tutor && {
          "TUTOR LAST NAME": tutor.last_name.value,
          "TUTOR FIRST NAME": tutor.first_name.value,
          "TUTOR STREET ADDRESS": tutor.address.value,
          "TUTOR CITY": tutor.city.value,
          "TUTOR PROVINCE": tutor.province.value,
          "TUTOR ZIP": tutor.zip.value,
        }),
      },
    }),
    redirect: "follow",
  });

  if (response.status !== 200) {
    throw new Error("Failed to submit waiver form");
  }

  const data = await response.json();
  return data.id || data.email;
};

/**
 * Submits the waiver signature and license to the API
 * @param signature - Signature informations
 * @param license - License information
 * @param entry_id - Entry id of the signer
 * @param instance_key - Instance key of the event
 * @param tutor_signature - Tutor's Signature informations
 * @returns Returns a boolean value indicating if the waiver has been submitted
 */
const submitWaiverExtraData = async (
  signature: Signature,
  license: License,
  entry_id: string,
  instance_key: string,
  tutor_signature?: Signature,
): Promise<boolean> => {
  const response = await fetch(
    cincolabdataEndpoints.public.waiver(instance_key),
    {
      method: "POST",
      headers: prepareHeaders(),
      mode: "cors",
      redirect: "follow",
      body: JSON.stringify({
        signature,
        license,
        date: new Date(signature.date),
        entry_id,
        instance_key,
        tutor_signature,
      }),
    },
  );

  if (response.status !== 200) {
    throw new Error("Failed to submit waiver signature and license");
  }
  const data = await response.json();
  return !!data.email;
};

async function blobToDataUrl(blob: Blob): Promise<string> {
  const e = await new Promise((r) => {
    const a = new FileReader();
    a.onload = r;
    a.readAsDataURL(blob);
  });
  return ((e as unknown as { target: FileReader }).target as FileReader)
    .result as string;
}

/**
 * Submits the waiver to the API
 * @param instanceKey - Instance key of the event
 * @param testDriver - Test driver informations
 * @param tutor - Tutor informations
 * @param locale - The locale
 * @returns Returns a boolean value indicating if the waiver has been submitted
 */
export const submitWaiver = async (
  instanceKey: string,
  testDriver: TestDriver,
  tutor?: Tutor,
  locale: "en" | "fr" = "en",
): Promise<boolean> => {
  // Check if activation exists
  try {
    await getUserByEmail(testDriver.email.value, instanceKey);
    // If user does exist, we do nothing (we dont want to have twice it's data)
  } catch (_error) {
    // Create activation
    await submitActivation({
      instance_key: instanceKey,
      email: testDriver.email.value,
      first_name: testDriver.first_name.value,
      last_name: testDriver.last_name.value,
      marketing_opt_in: testDriver.marketing_opt_in.value,
      locale: locale,
    });
  }

  const waiverInstanceKey = instanceKey.replace(
    "FORD_MODEL_E_DIGITAL_ACTIVATION",
    "FORD_MODEL_E_DIGITAL_WAIVER",
  );
  // Add entry to waiver form
  const entryId = await submitWaiverForm(waiverInstanceKey, testDriver, tutor);

  const front_image = await blobToDataUrl(
    testDriver.driver_license_front.value!,
  );
  const back_image = await blobToDataUrl(testDriver.driver_license_back.value!);

  // Send waiver to API
  await submitWaiverExtraData(
    {
      image: testDriver.signature.value,
      date: testDriver.date.value,
    },
    {
      front_image,
      back_image,
    },
    entryId,
    waiverInstanceKey,
    tutor
      ? {
          image: tutor.signature.value,
          date: testDriver.date.value,
        }
      : undefined,
  );

  return true;
};

/**
 * Returns an empty waiver form
 * @returns Returns an empty waiver form
 */
export const getWaiverForm = (): WaiverForm => {
  const today = new Date().toISOString().split("T")[0].split("-").join("-");

  return {
    test_driver: {
      signature: {
        value: "",
        required: true,
        error: "",
      },
      first_name: {
        value: "",
        required: true,
        error: "",
      },
      last_name: {
        value: "",
        required: true,
        error: "",
      },
      email: {
        value: "",
        required: true,
        error: "",
      },
      driver_license: {
        value: "",
        required: true,
        error: "",
      },
      driver_license_issuer: {
        value: "",
        required: true,
        error: "",
      },
      driver_license_front: {
        value: null,
        required: true,
        error: "",
        is_full: true,
      },
      driver_license_back: {
        value: null,
        required: true,
        error: "",
      },
      phone: {
        value: "",
        required: false,
        error: "",
      },
      address: {
        value: "",
        required: false,
        error: "",
      },
      city: {
        value: "",
        required: false,
        error: "",
      },
      province: {
        value: "",
        required: true,
        error: "",
      },
      zip: {
        value: "",
        required: false,
        error: "",
      },
      is_driver: {
        value: true,
        required: true,
        error: "",
        is_full: true,
      },
      vehicule: {
        value: "",
        required: true,
        error: "",
        is_full: true,
        options: [
          {
            label: { en: "F-150 Lightning", fr: "F-150 Lightning" },
            value: "F150BEV",
          },
          {
            label: { en: "Mustang Mach-E", fr: "Mustang Mach-E" },
            value: "MACHE",
          },
          {
            label: { en: "Both", fr: "Les deux" },
            value: "BOTH",
          },
        ],
      },
      marketing_opt_in: {
        value: false,
        required: false,
        error: "",
      },
      date: {
        value: today,
        required: false,
        error: "",
      },
      driver_license_shown: {
        value: false,
        required: false,
        error: "",
      },
    },
    tutor: {
      signature: {
        value: "",
        required: true,
        error: "",
      },
      first_name: {
        value: "",
        required: false,
        error: "",
      },
      last_name: {
        value: "",
        required: false,
        error: "",
      },
      address: {
        value: "",
        required: false,
        error: "",
      },
      city: {
        value: "",
        required: false,
        error: "",
      },
      province: {
        value: "",
        required: false,
        error: "",
      },
      zip: {
        value: "",
        required: false,
        error: "",
      },
    },
  };
};
